import * as React from 'react'

import styled, { css } from 'styled-components'
import { useInView } from 'react-intersection-observer'

import * as ds from '@bob/design-system'
import { styles as S } from '@bob/design-system'
import { BrushTitleAnimated } from '@bob/components'
import { SITE_URL } from '@bob/config'

import { Appear } from '../Appear'
import { Brush } from '../Brush'
import { SignatoryCount } from './SignatoryCount'

export type WhyProps = React.HTMLAttributes<HTMLDivElement>

function WhyComponent({ ...rest }: WhyProps): React.ReactElement {
  const inViewOptions = {
    threshold: 0.0,
    rootMargin: `0px 0px -40% 0px`,
    initialInView: false,
    triggerOnce: true,
  }

  const [$part1, inViewPart1] = useInView(inViewOptions)
  const [$part2, inViewPart2] = useInView(inViewOptions)

  const titles = [
    {
      inView: inViewPart1,
      text: 'Des marques engagées pour un monde où le bien est la norme et non plus l’exception.',
      Component: S.heading.Title2,
      brushes: [
        {
          wordIndex: 8,
          comp: Brush,
          props: { brushId: 'pink' },
        },
        {
          wordIndex: 11,
          comp: Brush,
          props: { brushId: 'green' },
        },
      ],
    },
    {
      inView: inViewPart2,
      text: 'Les marques signataires s’engagent auprès de vous',
      tag: 'h2',
      Component: S.heading.Title2,
      brushes: [
        {
          wordIndex: 3,
          comp: Brush,
          props: { brushId: 'yellow' },
        },
      ],
    },
  ]

  return (
    <WhySection {...rest}>
      <WhyPart1 ref={$part1} inView={inViewPart1}>
        <BrushTitleAnimated {...titles[0]} />
        <Appear isVisible={inViewPart1} delay={1}>
          <SignatoryCount />
        </Appear>
        <ImageText inView={inViewPart1}>
          <FlexCentered>
            <Appear isVisible={inViewPart1} delay={1.2}>
              <p>
                Toutes ont été pensées par des femmes et des hommes qui partagent la même ambition : avoir le meilleur
                impact possible sur la planète et les humains.
              </p>
              <p>
                Toutes sont engagées dans une démarche sociale ET environnementale. Pour nous, ce sont les deux piliers
                d’un monde plus équilibré : sans l’un, l’autre bascule.
              </p>
            </Appear>
          </FlexCentered>
          <ImageTextPictureContainer>
            <Appear isVisible={inViewPart1} delay={1.4}>
              <ds.Picture
                src={SITE_URL + '/assets/rasterized/manifesto/manifest-03_1x.png'}
                srcSet={`${SITE_URL + '/assets/rasterized/manifesto/manifest-03_1x.png'} 1x, ${
                  SITE_URL + '/assets/rasterized/manifesto/manifest-03_2x.png'
                } 2x, ${SITE_URL + '/assets/rasterized/manifesto/manifest-03_3x.png'} 3x `}
                width="630"
                height="575"
              />
            </Appear>
            <Appear isVisible={inViewPart1} delay={1.3} hasTranslate={false}>
              <ds.stickers.criteria.GoodForPlanet />
            </Appear>
          </ImageTextPictureContainer>
        </ImageText>
      </WhyPart1>
      <WhyPart2 ref={$part2} inView={inViewPart2}>
        <BrushTitleAnimated {...titles[1]} tag="h2" />
        <ImageText inView={inViewPart1}>
          <ImageTextPictureContainer>
            <Appear isVisible={inViewPart2} delay={1}>
              <ds.Picture src={SITE_URL + '/assets/rasterized/manifesto/manifeste-02.jpg'} width="630" height="575" />
            </Appear>
            <Appear isVisible={inViewPart2} delay={1.2} hasTranslate={false}>
              <ds.stickers.criteria.MoreHuman />
            </Appear>
          </ImageTextPictureContainer>
          <S.copy.Text5>
            <Appear isVisible={inViewPart2} delay={1.3}>
              <p>
                La transparence, c’est la base de tout. Signer la charte Bien ou Bien, c’est s’engager à dire où,
                pourquoi, comment et par qui les produits ont été fabriqués. Et surtout, ces marques signataires nous
                partagent en toute sincérité le chemin qu’il leur reste à parcourir : les axes d’amélioration et leurs
                engagements futurs.
              </p>

              <p>
                Nombre de ces marques sont en effet transformatrices de filières entières : cela prend parfois du temps
                de relocaliser certains circuits de production, de sourcer des matières plus responsables, etc. D’où
                l’importance de décrire en toute transparence le chemin déjà parcouru et celui qui reste à faire.
              </p>
            </Appear>
          </S.copy.Text5>
        </ImageText>
      </WhyPart2>
    </WhySection>
  )
}

const FlexCentered = styled(S.copy.Text5)`
  @media screen and (${ds.breakpoint.TABLET}) {
    display: flex;
    align-items: center;
  }
`

export const Why = styled(WhyComponent)`
  ${S.copy.Text5} {
    font-size: 18px;
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    ${S.copy.Text5} {
      font-size: 24px;
    }
  }
`

const WhySection = styled.section`
  position: relative;
  overflow: hidden;
  padding-top: 35px;
  padding-bottom: 92px;

  ${S.heading.Title2} {
    text-align: center;
    margin-bottom: 25px;
  }

  ${ds.stickers.Sticker} {
    position: absolute;
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    padding-top: 64px;

    ${S.heading.Title2} {
      margin: 0 ${(110 / 1280) * 100}% ${(45 / 1280) * 100}%;
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP_L}) {
    ${ds.stickers.Sticker} {
      width: 182px;
      height: 182px;
    }
  }
`

const WhyPart1 = styled(S.container.Base)<{ inView: boolean }>`
  ${ds.stickers.Sticker} {
    left: 0;
    bottom: 0;
    transform: translate(${(60 / 182) * 100}%, ${(50 / 182) * 100}%);
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    ${ds.stickers.Sticker} {
      transform: translate(${(80 / 182) * 100}%, ${(100 / 182) * 100}%);
    }

    ${S.copy.Text5} {
      padding-top: 0;
    }
  }
`

const WhyPart2 = styled(S.container.Base)<{ inView: boolean }>`
  margin-top: 65px;

  ${ds.stickers.Sticker} {
    top: 0;
    right: 0;
    transform: translate(${(15 / 125) * 100}%, ${(-30 / 125) * 100}%);
  }

  ${S.copy.Text5} {
    padding-top: 15px;
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    margin-top: 150px;

    ${ds.stickers.Sticker} {
      transform: translate(${(106 / 182) * 100}%, ${(-43 / 182) * 100}%);
    }

    ${S.copy.Text5} {
      padding-top: 0;
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP_L}) {
    ${S.copy.Text5} {
      padding-top: 157px;
    }
  }
`

const ImageTextPictureContainer = styled.div`
  position: relative;
  align-self: flex-start;
  grid-row: 1 / span 1;

  ${ds.Picture} {
    width: ${(395 / 335) * 100}%;
    margin-left: 15px;
    border-radius: 12px;
  }

  &:first-child {
    ${ds.Picture} {
      margin-right: 0;
      margin-left: -78px;
    }
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    grid-row: auto;

    ${ds.Picture} {
      width: ${(630 / 515) * 100}%;
      margin-right: -${(630 / 515) * 100 - 100}%;
    }

    &:first-child {
      grid-column: 1 / span 5;

      ${ds.Picture} {
        margin-right: 0;
        margin-left: -${(630 / 515) * 100 - 100}%;
      }
    }

    &:last-child {
      grid-column: 8 / -1;
    }
  }
`

const ImageText = styled.div<{ inView?: boolean }>`
  display: grid;
  gap: 20px;

  ${S.copy.Text5} {
    p {
      margin: 1.5em 0;

      &:first-child {
        margin-top: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  ${S.copy.Text5},
  ${ImageTextPictureContainer} {
    opacity: 0;
    transform: translateY(30px);
    transition-delay: 100ms;
    transition-duration: 350ms;
    transition-property: opacity;
    transition-timing-function: ease-out;

    ${({ inView }) =>
      inView &&
      css`
        transform: translateY(0);
        opacity: 1;
      `}
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    grid-template-columns: repeat(12, 1fr);

    ${S.copy.Text5} {
      &:first-child {
        grid-column: 1 / span 6;
      }

      &:last-child {
        grid-column: 7 / span 6;
      }
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    ${S.copy.Text5} {
      &:first-child {
        grid-column: 2 / span 5;
      }

      &:last-child {
        grid-column-end: span 5;
      }
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP_L}) {
    gap: 36px;
  }
`
